import React, { useState, useEffect } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";

// Function to check if an icon URL is valid
const isIconAvailable = (iconUrl) => {
  const img = new Image();
  img.src = iconUrl;
  return img.complete;
};

const Map = ({ villages }) => {
  const [villagesWithPosition, setVillagesWithPosition] = useState([]);
  const [map, setMap] = useState(null);
  const [markers, setMarkers] = useState([]);
  const [fireMarkers, setFireMarkers] = useState([]); // Track fire markers

  // Initialize the map
  useEffect(() => {
    const leafletMap = L.map("map", {
      center: [50, 50], // Center the map
      zoom: 0,
      crs: L.CRS.Simple, // Use a simple 2D coordinate system
      zoomControl: false, // Disable zoom control for custom control
      scrollWheelZoom: false, // Allow zoom with scroll wheel
      touchZoom: false, // Enable touch zooming
      doubleClickZoom: false, // Disable zoom via double-click
      boxZoom: false, // Disable zoom using box zoom
    });

    const bounds = [
      [-5000, -5000], // Bottom-left corner
      [5000, 5000],   // Top-right corner
    ];

    leafletMap.setMaxBounds(bounds); // Prevent scrolling beyond the defined area

    // Add mouse position display
    leafletMap.on("mousemove", (e) => {
      const { lat, lng } = e.latlng;
      console.log(`Mouse Position: Latitude: ${lat}, Longitude: ${lng}`);
    });

    setMap(leafletMap);

    return () => {
      leafletMap.remove(); // Clean up map on unmount
    };
  }, []);

  // Generate positions for the villages with the same icon for each village
  useEffect(() => {
    const generateVillagePositions = () =>
      villages.map((village) => ({
        ...village,
        position: [
          Math.random() * 5000 - 2500, // Random x-coordinate within bounds
          Math.random() * 5000 - 2500, // Random y-coordinate within bounds
        ],
        icon: "https://stokefiremap.vercel.app/images/v2.png", // Use the second icon (v2.png)
      }));

    setVillagesWithPosition(generateVillagePositions());
  }, [villages]);

  // Create the map markers, fire markers, and roads (paths)
  useEffect(() => {
    if (!map) return;

    // Clear existing markers, fire markers, and paths
    markers.forEach((marker) => map.removeLayer(marker));
    fireMarkers.forEach((fireMarker) => map.removeLayer(fireMarker));

    const createMarker = (village) => {
      const icon = L.icon({
        iconUrl: village.icon,
        iconSize: [40, 40], // Default size of the icon
        iconAnchor: [25, 25], // Center the icon
      });

      const marker = L.marker(village.position, { icon }).addTo(map);
      const playerPic = village.playerPic || "/images/user.png";

      // Add popup with village details
      marker.bindPopup(`
        <div style="font-size: 12px; width: 200px;">
            <img src="${playerPic}" width="100px" height="100px" style="border-radius: 50%"/>
            <a href="https://warpcast.com/${village.playerUsername}" target='_blank'>
              <img src="/images/warpcast.png" width="30px" height="30px" style="position: absolute; right: 30px;top:30px;cursor:pointer;"/>
            </a>
            <p style="font-size: 20px;"><strong>${village.playerUsername}</strong> </p>
            <p><strong>Score:</strong> ${village.score} 🏆</p>
            <p><strong>Villagers:</strong> ${village.villagers} 👥</p>
            <p><strong>Huts:</strong> ${village.huts} 🛖</p>
            <p><strong>Food:</strong> ${village.food} 🍖</p>
            <p><strong>Wood:</strong> ${village.wood} 🪵</p>
            <p><strong>Lumber Camps:</strong> ${village.lumberCamps} ⛺️</p>
        </div>
      `);

      return marker;
    };

    // Create new markers for all villages
    const newMarkers = villagesWithPosition.map(createMarker);
    setMarkers(newMarkers); // Store the village markers

    // Create a fire marker for each village (ensure only one fire per village)
    const createFireMarker = (village) => {
      const fireIcon = L.icon({
        iconUrl: "/images/fire.gif", // Fire GIF image
        iconSize: [15, 15], // Set size of the fire image
        iconAnchor: [25, 25], // Anchor at the center of the fire image
      });

      // Place the fire marker next to the village marker (adjust the distance as needed)
      const firePosition = [
        village.position[0],
        village.position[1] + 5, // Slightly offset the fire position for visibility
      ];

      const fireMarker = L.marker(firePosition, { icon: fireIcon }).addTo(map);
      return fireMarker;
    };

    // Create fire markers for all villages and store them
    const newFireMarkers = villagesWithPosition.map(createFireMarker);
    setFireMarkers(newFireMarkers); // Store the fire markers

    // Connect villages with paths (lines)
    const connectVillages = () => {
      // Create a line connecting all village markers (if there are multiple villages)
      const villagePositions = villagesWithPosition.map((village) => village.position);

      if (villagePositions.length > 1) {
        const roadPath = L.polyline(villagePositions, {
          color: "orange",  // Line color
          weight: 2,      // Line weight (thickness)
          opacity: 0.1,   // Line opacity
        }).addTo(map);

        // Add mouseover and mouseout event listeners to change opacity
        roadPath.on("mouseover", () => {
          roadPath.setStyle({ opacity: 0.5 }); // Increase opacity when hovered
        });

        roadPath.on("mouseout", () => {
          roadPath.setStyle({ opacity: 0.1 }); // Reset opacity when mouse leaves
        });
      }
    };

    // Call the function to draw roads after the markers are added
    connectVillages();

  }, [map, villagesWithPosition]);

  return (
    <div>
      {/* Map container */}
      <div
        id="map"
        style={{
          height: "100vh",
          width: "100%",
          backgroundImage: `url('/images/txt.jpg')`, // Texture as a background image
          backgroundSize: "100px 100px", // Tile size
          backgroundRepeat: "repeat", // Repeat the texture
        }}
      ></div>
    </div>
  );
};

export default Map;
